/**
 * Appointment Detail Page
 *
 * View appointment details and manage status transitions
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Stethoscope,
  FileImage,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  RotateCcw,
} from 'lucide-react'

import {
  useAppointment,
  useConfirmAppointment,
  useCheckInAppointment,
  useStartAppointment,
  useCompleteAppointment,
  useCancelAppointment,
  useMarkNoShow,
  useScheduleAppointment,
  useDeleteAppointment,
} from '@/application/appointments/useAppointments'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { ConfirmDialog } from '@/presentation/components/ui/ConfirmDialog'
import { Button } from '@/presentation/components/ui/Button'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Input } from '@/presentation/components/ui/Input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { APP_ROUTES } from '@/config/constants'
import {
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUSES,
  BOOKED_VIA_LABELS,
} from '@/domain/appointment/types'

// =============================================================================
// Helper Functions
// =============================================================================

function formatDate(dateString: string): string {
  return new Date(dateString + 'T00:00:00').toLocaleDateString('es-CO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatTime(time: string): string {
  return time.substring(0, 5)
}

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 rounded-lg bg-[var(--secondary)]">{icon}</div>
      <div>
        <p className="text-sm text-[var(--foreground-muted)]">{label}</p>
        <p className="font-medium">{value || 'No registrado'}</p>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function AppointmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: appointment, isLoading, isError, error } = useAppointment(id)

  // Mutations
  const scheduleMutation = useScheduleAppointment()
  const confirmMutation = useConfirmAppointment()
  const checkInMutation = useCheckInAppointment()
  const startMutation = useStartAppointment()
  const completeMutation = useCompleteAppointment()
  const cancelMutation = useCancelAppointment()
  const noShowMutation = useMarkNoShow()
  const deleteMutation = useDeleteAppointment()

  // Dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
        <p className="text-[var(--foreground-muted)] mt-4">Cargando cita...</p>
      </div>
    )
  }

  if (isError || !appointment) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-[var(--destructive)] mb-4" />
        <h3 className="text-lg font-semibold">Error al cargar cita</h3>
        <p className="text-[var(--foreground-muted)] mt-1">{error?.message || 'Cita no encontrada'}</p>
        <Button onClick={() => navigate(APP_ROUTES.APPOINTMENTS)} className="mt-4">
          Volver a citas
        </Button>
      </div>
    )
  }

  const status = appointment.status

  // Action handlers
  const handleTransition = (
    mutation: { mutate: (arg: any, opts: any) => void; isPending: boolean },
    arg: any,
    successMsg: string
  ) => {
    mutation.mutate(arg, {
      onSuccess: () => toast.success(successMsg),
      onError: (err: unknown) => toast.error(getErrorMessage(err)),
    })
  }

  const handleCancel = () => {
    if (!cancelReason.trim()) {
      toast.error('La razón de cancelación es requerida')
      return
    }
    cancelMutation.mutate(
      { id: appointment.id, reason: cancelReason },
      {
        onSuccess: () => {
          toast.success('Cita cancelada')
          setCancelDialogOpen(false)
          setCancelReason('')
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(appointment.id, {
      onSuccess: () => {
        toast.success('Cita eliminada')
        navigate(APP_ROUTES.APPOINTMENTS)
      },
      onError: (err) => toast.error(getErrorMessage(err)),
    })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--foreground)]">
              Cita - {appointment.patientName}
            </h1>
            <p className="text-[var(--foreground-muted)]">
              {formatDate(appointment.scheduledDate)}
            </p>
          </div>
        </div>
        <Badge
          variant={APPOINTMENT_STATUS_COLORS[status] as any}
          className="text-sm px-3 py-1"
        >
          {APPOINTMENT_STATUS_LABELS[status]}
        </Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Appointment Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" /> Detalles de la Cita
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <InfoItem
                  icon={<Calendar className="h-4 w-4 text-[var(--primary)]" />}
                  label="Fecha"
                  value={formatDate(appointment.scheduledDate)}
                />
                <InfoItem
                  icon={<Clock className="h-4 w-4 text-[var(--primary)]" />}
                  label="Horario"
                  value={`${formatTime(appointment.scheduledTime)} - ${formatTime(appointment.endTime)}`}
                />
                <InfoItem
                  icon={<FileImage className="h-4 w-4 text-[var(--primary)]" />}
                  label="Modalidad"
                  value={`${appointment.modalityName} (${appointment.modalityCode})`}
                />
                <InfoItem
                  icon={<Stethoscope className="h-4 w-4 text-[var(--primary)]" />}
                  label="Origen"
                  value={BOOKED_VIA_LABELS[appointment.bookedVia]}
                />
              </div>
            </CardContent>
          </Card>

          {/* Patient Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" /> Paciente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <InfoItem
                  icon={<User className="h-4 w-4 text-[var(--primary)]" />}
                  label="Nombre"
                  value={appointment.patientName}
                />
                <InfoItem
                  icon={<FileImage className="h-4 w-4 text-[var(--primary)]" />}
                  label="Documento"
                  value={appointment.patientDocument}
                />
              </div>
            </CardContent>
          </Card>

          {/* Provider Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5" /> Profesional
              </CardTitle>
            </CardHeader>
            <CardContent>
              <InfoItem
                icon={<User className="h-4 w-4 text-[var(--primary)]" />}
                label="Nombre"
                value={appointment.providerName}
              />
            </CardContent>
          </Card>

          {/* Notes */}
          {appointment.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notas</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[var(--foreground-muted)]">{appointment.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Cancellation Reason */}
          {appointment.cancellationReason && (
            <Card>
              <CardHeader>
                <CardTitle className="text-[var(--destructive)]">Razón de cancelación</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[var(--foreground-muted)]">{appointment.cancellationReason}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar: Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Acciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* REQUESTED → SCHEDULED */}
              {status === APPOINTMENT_STATUSES.REQUESTED && (
                <Button
                  className="w-full"
                  onClick={() => handleTransition(scheduleMutation, appointment.id, 'Cita programada')}
                  isLoading={scheduleMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Programar
                </Button>
              )}

              {/* SCHEDULED → CONFIRMED */}
              {status === APPOINTMENT_STATUSES.SCHEDULED && (
                <Button
                  className="w-full"
                  onClick={() => handleTransition(confirmMutation, appointment.id, 'Cita confirmada')}
                  isLoading={confirmMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmar
                </Button>
              )}

              {/* CONFIRMED → CHECKED_IN */}
              {status === APPOINTMENT_STATUSES.CONFIRMED && (
                <Button
                  className="w-full"
                  onClick={() => handleTransition(checkInMutation, appointment.id, 'Check-in registrado')}
                  isLoading={checkInMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Registrar Llegada
                </Button>
              )}

              {/* CHECKED_IN → IN_PROGRESS */}
              {status === APPOINTMENT_STATUSES.CHECKED_IN && (
                <Button
                  className="w-full"
                  onClick={() => handleTransition(startMutation, appointment.id, 'Cita iniciada')}
                  isLoading={startMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Iniciar
                </Button>
              )}

              {/* IN_PROGRESS → COMPLETED */}
              {status === APPOINTMENT_STATUSES.IN_PROGRESS && (
                <Button
                  className="w-full"
                  onClick={() => handleTransition(completeMutation, appointment.id, 'Cita completada')}
                  isLoading={completeMutation.isPending}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Completar
                </Button>
              )}

              {/* Cancel (from any active status) */}
              {([APPOINTMENT_STATUSES.REQUESTED, APPOINTMENT_STATUSES.SCHEDULED, APPOINTMENT_STATUSES.CONFIRMED, APPOINTMENT_STATUSES.CHECKED_IN, APPOINTMENT_STATUSES.IN_PROGRESS] as readonly typeof status[]).includes(status) && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Cancelar Cita
                </Button>
              )}

              {/* No Show */}
              {status === APPOINTMENT_STATUSES.CONFIRMED && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleTransition(noShowMutation, appointment.id, 'Marcado como no asistió')}
                  isLoading={noShowMutation.isPending}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  No Asistió
                </Button>
              )}

              {/* Reschedule */}
              {([APPOINTMENT_STATUSES.REQUESTED, APPOINTMENT_STATUSES.SCHEDULED, APPOINTMENT_STATUSES.CONFIRMED] as readonly typeof status[]).includes(status) && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`${APP_ROUTES.APPOINTMENT_NEW}?rescheduleFrom=${appointment.id}`)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reprogramar
                </Button>
              )}

              {/* Study Link */}
              {appointment.studyId && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/studies/${appointment.studyId}`)}
                >
                  <FileImage className="h-4 w-4 mr-2" />
                  Ver Estudio
                </Button>
              )}

              {/* Delete */}
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Eliminar
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Cita</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              label="Razón de cancelación"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ingrese la razón..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Volver
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              isLoading={cancelMutation.isPending}
            >
              Cancelar Cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar cita"
        description="¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
